import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, LoaderCircle, MessageCircleMore, ReceiptText, RefreshCw, UserRound, X,
} from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import notificationService from '../services/notification.service';
import type { Notification } from '../services/notification.service';

const iconFor = (type: string) => {
  if (type === 'message') return <MessageCircleMore size={17} />;
  if (type.includes('payment')) return <ReceiptText size={17} />;
  if (type === 'staff_assigned') return <UserRound size={17} />;
  return <RefreshCw size={17} />;
};

export default function PortalNotifications() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications({ page: 1, page_size: 8 });
      setItems(response.notifications);
      setUnread(response.unread_count);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const routeFor = (notification: Notification) => {
    if (notification.notification_type === 'message' || notification.notification_type.includes('document')) return '/app/chat';
    if (notification.notification_type.includes('payment') || notification.notification_type === 'action_required') return '/app/payments';
    return '/app/application';
  };

  const handleRead = async (notification: Notification) => {
    if (!notification.is_read) {
      try {
        await notificationService.markAsRead([notification.id]);
        setUnread((value) => Math.max(0, value - 1));
        setItems((current) => current.map((item) => item.id === notification.id ? { ...item, is_read: true } : item));
      } catch {
        // Keep the item visible; the next refresh can resolve transient failures.
      }
    }
    setOpen(false);
    navigate(routeFor(notification));
  };

  const markAll = async () => {
    await notificationService.markAllAsRead();
    setItems((value) => value.map((item) => ({ ...item, is_read: true })));
    setUnread(0);
  };

  return (
    <div className="portal-notifications" ref={rootRef}>
      <button
        className="portal-icon-button"
        aria-label={language === 'ar' ? 'الإشعارات' : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => { setOpen((value) => !value); if (!open) void load(); }}
      >
        <Bell size={20} />
        {unread > 0 && <b className="portal-notification-count">{unread > 9 ? '9+' : unread}</b>}
      </button>

      {open && (
        <div className="portal-notification-panel" role="dialog" aria-label={language === 'ar' ? 'الإشعارات' : 'Notifications'}>
          <header>
            <div><strong>{language === 'ar' ? 'الإشعارات' : 'Notifications'}</strong>{unread > 0 && <span>{unread}</span>}</div>
            <div className="portal-notification-actions">
              {unread > 0 && <button onClick={() => void markAll()}><CheckCheck size={15} />{language === 'ar' ? 'قراءة الكل' : 'Mark all'}</button>}
              <button onClick={() => setOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>
          </header>

          <div className="portal-notification-list">
            {loading && !items.length ? (
              <LoaderCircle className="portal-spin portal-notification-loading" size={22} />
            ) : items.length === 0 ? (
              <div className="portal-notification-empty"><Bell size={22} /><p>{language === 'ar' ? 'لا توجد إشعارات بعد' : 'No notifications yet'}</p></div>
            ) : items.map((notification) => (
              <button
                key={notification.id}
                className={notification.is_read ? '' : 'unread'}
                onClick={() => void handleRead(notification)}
              >
                <span>{iconFor(notification.notification_type)}</span>
                <span>
                  <strong>{language === 'ar' ? notification.title_ar : notification.title}</strong>
                  <small>{language === 'ar' ? notification.message_ar : notification.message}</small>
                  <em>{new Date(notification.created_at).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</em>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
